<template>
  <div class="regional-chart-container">
    <div class="chart-header">
      <h3 class="chart-title">Regional Power Capacity</h3>
      <p class="chart-subtitle">Chinese vs Non-Chinese Capital Investment</p>
    </div>
    <div ref="chartRef" class="chart-svg-container"></div>
    <div class="chart-legend">
      <div class="legend-item">
        <span class="legend-color legend-chinese"></span>
        <span class="legend-label">Chinese Capital</span>
      </div>
      <div class="legend-item">
        <span class="legend-color legend-non-chinese"></span>
        <span class="legend-label">Non-Chinese Capital</span>
      </div>
    </div>
    <p class="chart-caption">
      Values show capacity in MW and percentage of regional total
    </p>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import * as d3 from 'd3';

export default {
  name: 'RegionalCapacityChart',
  props: {
    dataCenters: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const chartRef = ref(null);
    let resizeObserver = null;

    // Regional classification logic (matching R script)
    const classifyRegion = (dc) => {
      if (dc.country === 'Singapore') return 'Singapore';

      const city = (dc.city || '').toLowerCase();

      // Johor region
      if (/johor|kulai|iskandar|nusajaya|johor bahru/.test(city)) {
        return 'Johor';
      }

      // Klang Valley region
      if (/kuala lumpur|cyberjaya|petaling|shah alam|bukit jalil|subang|klang|sentul/.test(city)) {
        return 'Klang Valley';
      }

      return null; // Exclude other regions
    };

    // Capital classification logic (matching R script)
    const classifyCapital = (dc) => {
      const chCapital = dc.ch_capital || '';
      // Chinese capital includes "純中資" (pure Chinese) and "合資" (joint venture)
      if (chCapital === '純中資' || chCapital === '合資') {
        return 'Chinese Capital';
      }
      return 'Non-Chinese Capital';
    };

    // Compute regional data
    const regionalData = computed(() => {
      const regions = {};

      props.dataCenters.forEach(dc => {
        // Only include datacenters with estimated_capacity (matching R script logic)
        // Note: Future versions will update the source data to include all facilities
        const capacity = dc.estimated_capacity;
        if (!capacity || capacity <= 0) return;

        const region = classifyRegion(dc);
        if (!region) return;

        const capitalType = classifyCapital(dc);

        if (!regions[region]) {
          regions[region] = {
            name: region,
            'Chinese Capital': 0,
            'Non-Chinese Capital': 0,
            total: 0
          };
        }

        regions[region][capitalType] += capacity;
        regions[region].total += capacity;
      });

      // Convert to array and sort by total capacity descending
      return Object.values(regions)
        .map(r => ({
          ...r,
          chinesePercent: r.total > 0 ? (r['Chinese Capital'] / r.total * 100).toFixed(1) : 0,
          nonChinesePercent: r.total > 0 ? (r['Non-Chinese Capital'] / r.total * 100).toFixed(1) : 0
        }))
        .sort((a, b) => b.total - a.total);
    });

    // Draw chart with D3
    const drawChart = () => {
      if (!chartRef.value || regionalData.value.length === 0) return;

      // Clear previous chart
      d3.select(chartRef.value).selectAll('*').remove();

      const container = chartRef.value;
      const containerWidth = container.clientWidth;

      // Responsive dimensions
      const margin = { top: 20, right: 120, bottom: 50, left: 100 };
      const width = Math.max(300, containerWidth - margin.left - margin.right);
      const barHeight = 45;
      const barGap = 25;
      const barsHeight = regionalData.value.length * (barHeight + barGap) - barGap;
      const height = barsHeight + margin.top + margin.bottom;

      // Create SVG
      const svg = d3.select(chartRef.value)
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height)
        .append('g')
        .attr('transform', `translate(${margin.left}, ${margin.top})`);

      // Scales
      const maxCapacity = d3.max(regionalData.value, d => d.total);
      const xScale = d3.scaleLinear()
        .domain([0, maxCapacity])
        .range([0, width]);

      const yScale = d3.scaleBand()
        .domain(regionalData.value.map(d => d.name))
        .range([0, barsHeight])
        .padding(0.3);

      // Colors (minimalist black/white/gray with red accent for Chinese)
      const colors = {
        'Chinese Capital': '#dc2626',      // Red for emphasis
        'Non-Chinese Capital': '#94a3b8'   // Muted gray
      };

      // Draw bars for each region
      regionalData.value.forEach((region, i) => {
        const y = yScale(region.name);
        let xOffset = 0;

        // Region label (left side)
        svg.append('text')
          .attr('x', -10)
          .attr('y', y + yScale.bandwidth() / 2)
          .attr('text-anchor', 'end')
          .attr('dominant-baseline', 'middle')
          .attr('class', 'region-label')
          .style('font-size', '14px')
          .style('font-weight', '600')
          .style('fill', '#000000')
          .text(region.name);

        // Draw Non-Chinese bar first (background)
        const nonChineseWidth = xScale(region['Non-Chinese Capital']);
        if (nonChineseWidth > 0) {
          svg.append('rect')
            .attr('x', xOffset)
            .attr('y', y)
            .attr('width', nonChineseWidth)
            .attr('height', yScale.bandwidth())
            .attr('fill', colors['Non-Chinese Capital'])
            .attr('rx', 0);

          // Label for Non-Chinese (if large enough)
          if (nonChineseWidth > 80) {
            svg.append('text')
              .attr('x', xOffset + nonChineseWidth / 2)
              .attr('y', y + yScale.bandwidth() / 2)
              .attr('text-anchor', 'middle')
              .attr('dominant-baseline', 'middle')
              .style('font-size', '12px')
              .style('font-weight', '600')
              .style('fill', '#ffffff')
              .text(`${region['Non-Chinese Capital'].toFixed(1)} MW (${region.nonChinesePercent}%)`);
          }

          xOffset += nonChineseWidth;
        }

        // Draw Chinese bar (foreground/emphasis)
        const chineseWidth = xScale(region['Chinese Capital']);
        if (chineseWidth > 0) {
          svg.append('rect')
            .attr('x', xOffset)
            .attr('y', y)
            .attr('width', chineseWidth)
            .attr('height', yScale.bandwidth())
            .attr('fill', colors['Chinese Capital'])
            .attr('rx', 0);

          // Label for Chinese Capital
          if (chineseWidth > 80) {
            // Large segment - label inside
            svg.append('text')
              .attr('x', xOffset + chineseWidth / 2)
              .attr('y', y + yScale.bandwidth() / 2)
              .attr('text-anchor', 'middle')
              .attr('dominant-baseline', 'middle')
              .style('font-size', '12px')
              .style('font-weight', '600')
              .style('fill', '#ffffff')
              .text(`${region['Chinese Capital'].toFixed(1)} MW (${region.chinesePercent}%)`);
          } else if (region['Chinese Capital'] > 0) {
            // Small segment - label outside
            svg.append('text')
              .attr('x', xOffset + chineseWidth + 8)
              .attr('y', y + yScale.bandwidth() / 2)
              .attr('text-anchor', 'start')
              .attr('dominant-baseline', 'middle')
              .style('font-size', '11px')
              .style('font-weight', '600')
              .style('fill', '#666666')
              .text(`${region['Chinese Capital'].toFixed(1)} MW (${region.chinesePercent}%)`);
          }
        }
      });

      // X-axis
      const xAxis = svg.append('g')
        .attr('transform', `translate(0, ${barsHeight + 15})`)
        .call(d3.axisBottom(xScale)
          .ticks(5)
          .tickFormat(d => `${d} MW`));

      xAxis.selectAll('text')
        .style('font-size', '12px')
        .style('fill', '#333333');

      // X-axis label
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', barsHeight + 45)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('fill', '#666666')
        .text('Total Datacenter Capacity (MW)');

      // Style axis lines
      svg.selectAll('.domain, .tick line')
        .style('stroke', '#cccccc');
    };

    // Watch for data changes
    watch(regionalData, () => {
      drawChart();
    }, { deep: true });

    onMounted(() => {
      drawChart();

      // Resize observer for responsiveness
      resizeObserver = new ResizeObserver(() => {
        drawChart();
      });
      if (chartRef.value) {
        resizeObserver.observe(chartRef.value);
      }
    });

    onUnmounted(() => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    });

    return {
      chartRef,
      regionalData
    };
  }
};
</script>

<style scoped>
.regional-chart-container {
  padding: 16px;
  background: #ffffff;
}

.chart-header {
  margin-bottom: 16px;
  text-align: center;
}

.chart-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #000000;
  margin: 0 0 4px 0;
}

.chart-subtitle {
  font-size: 0.875rem;
  color: #666666;
  margin: 0;
}

.chart-svg-container {
  width: 100%;
  min-height: 280px;
  display: flex;
  justify-content: center;
  overflow: visible;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 0;
}

.legend-chinese {
  background: #dc2626;
}

.legend-non-chinese {
  background: #94a3b8;
}

.legend-label {
  font-size: 0.875rem;
  color: #333333;
  font-weight: 500;
}

.chart-caption {
  font-size: 0.75rem;
  color: #666666;
  text-align: center;
  margin: 12px 0 0 0;
}
</style>
